import { Badge } from '@/components/Badge';
import { Card, CardBody, Stat } from '@/components/Card';
import type { Assessment } from '@/domain/engine';
import { formatCurrency, formatMass, formatRatio } from '@/lib/format';

const CARD_TONES: Record<Assessment['verdict']['level'], 'default' | 'accent' | 'warning' | 'critical'> = {
  infeasible: 'critical',
  risky: 'warning',
  feasible: 'default',
  good: 'accent',
  ample: 'accent',
  unknown: 'default',
};

/** 可行性结论 + 四项核心指标 */
export function VerdictBanner({ assessment }: { assessment: Assessment }) {
  const { verdict, twr, mass, thrust, cost, reservePct } = assessment;

  const massTotal = mass.value ? formatMass(mass.value.totalG) : null;
  const effective = twr.value?.effective ?? null;
  const thrustTotal = thrust.value ? formatMass(thrust.value.totalG) : null;

  return (
    <Card tone={CARD_TONES[verdict.level]} className="print-keep">
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={verdict.tone} solid={verdict.level !== 'unknown'}>
            {verdict.label}
          </Badge>
          <span className="tabular text-xs text-fg-muted">
            有效推重比 {formatRatio(effective)}
            <span className="mx-1 text-fg-subtle">·</span>
            预留 {formatRatio(reservePct, 0)}%
          </span>
        </div>

        <p className="text-xs leading-relaxed text-fg-muted">{verdict.description}</p>

        <div className="grid grid-cols-2 gap-x-3 gap-y-4 border-t border-line pt-3 md:grid-cols-4">
          <Stat
            label="预估总推力"
            value={thrustTotal?.value ?? '—'}
            unit={thrustTotal?.unit ?? 'g'}
            tone={thrust.value ? (thrust.value.source === 'input' ? 'neutral' : 'caution') : 'neutral'}
            hint={
              thrust.value
                ? thrust.value.source === 'input'
                  ? '按实测最大推力汇总'
                  : thrust.value.source === 'estimated'
                    ? '经验估算值 ±20%'
                    : '实测与估算混合'
                : '缺少推力数据'
            }
          />
          <Stat
            label="预估起飞质量"
            value={massTotal?.value ?? '—'}
            unit={massTotal?.unit ?? 'g'}
            hint={mass.value ? `空载 ${formatMass(mass.value.emptyG).value} ${formatMass(mass.value.emptyG).unit}` : '缺少重量数据'}
          />
          <Stat
            label="有效推重比"
            value={formatRatio(effective)}
            tone={
              effective === null
                ? 'neutral'
                : effective < 1
                  ? 'critical'
                  : effective < 1.2
                    ? 'warning'
                    : effective < 1.5
                      ? 'caution'
                      : 'ok'
            }
            hint="已扣除安全系数预留"
          />
          <Stat
            label="预估金额"
            value={formatCurrency(cost.value?.total ?? null)}
            hint={
              cost.value && cost.value.per100g !== null
                ? `${formatCurrency(cost.value.per100g)} / 100 g 起飞质量`
                : '缺少价格数据'
            }
          />
        </div>
      </CardBody>
    </Card>
  );
}
